import {parseISO} from "date-fns"

import {i18n} from "../../i18n.ts"
import {AKlaraDashboardBucket, AKlaraDashboardGranularityValues} from "../../services/types.generated.ts"
import {formatDate} from "./components/formatDate.ts"
import {customRangeDays, TKlaraRange} from "./period"

export type TKlaraGranularity = AKlaraDashboardGranularityValues

const MAX_DAILY_DAYS = 31
const MAX_WEEKLY_DAYS = 120

/** How finely a range is bucketed over time: by day up to a month, by week up to ~4 months, else by month. */
export function granularityFor(range: TKlaraRange): TKlaraGranularity {
  if (!range.from || !range.to) {
    return AKlaraDashboardGranularityValues.Month
  }
  const days = customRangeDays(range.from, range.to)
  if (days <= MAX_DAILY_DAYS) {
    return AKlaraDashboardGranularityValues.Day
  }
  return days <= MAX_WEEKLY_DAYS ? AKlaraDashboardGranularityValues.Week : AKlaraDashboardGranularityValues.Month
}

/**
 * "14 Oct" for a day, "Week to 19 Oct" for a week (its possibly clipped last day) and "Oct 2026" for a month.
 * Bucket dates are calendar days of the dashboard time zone, so they are formatted as they are.
 */
export function bucketLabel(bucket: AKlaraDashboardBucket, granularity: TKlaraGranularity): string {
  switch (granularity) {
    case AKlaraDashboardGranularityValues.Day:
      return formatDate(parseISO(bucket.start_date).getTime(), "d MMM")
    case AKlaraDashboardGranularityValues.Week:
      return i18n.t("KlaraDashboard_UpdatesChart_WeekTo", {
        date: formatDate(parseISO(bucket.end_date).getTime(), "d MMM"),
      })
    case AKlaraDashboardGranularityValues.Month:
      return formatDate(parseISO(bucket.start_date).getTime(), "MMM yyyy")
  }
}

export const bucketTotal = (bucket: AKlaraDashboardBucket) => bucket.voice + bucket.manual

/** The mean number of updates per bucket; 0 without buckets. */
export function averagePerBucket(buckets: readonly AKlaraDashboardBucket[]): number {
  if (buckets.length === 0) {
    return 0
  }
  return buckets.reduce((sum, bucket) => sum + bucketTotal(bucket), 0) / buckets.length
}
